import {
  findUserTokenInStorage,
  logInWithPassword,
  login,
  logOut,
  setUserToken,
  fakeSessionLogIn,
  setFakeSession,
  refreshUserToken,
  replaceWithGuestSession,
  isFakeSessionInFlight,
  settleFakeSessionFlight,
} from "./authActions";
import { Action } from "../../models/Action";
import { LOGIN_USER_URL } from "../../api/apiRoutes";
import { gaEvent } from "../../../utils/Analitics/ga";
import { GA_LOGIN } from "../../../utils/Analitics/gaEventsConsts";
import qs from "qs";
import { clearB2bHomePageUrl } from "../../../utils/b2bHomePageStorage";
import authInterceptor from "../../api/authInterceptor";
import { resetBasket } from "../basket/basketActions";
import { clearSurveyDraft } from "../../components/FinalizePage/BasketSurveyStep/utils/surveyDraftStorage";
import { SESSION_EXPIRED_ERROR_MESSAGE } from "../../utils/apiErrorMessages";
import { UserToken } from "../../models/UserToken";

export const USER_TOKEN = "userToken";

const isGuestName = (userName?: string) => !!userName && userName.substring(0, 5) === "GUEST";

const isTokenExpired = (token: any) => {
  if (!token?.tokenValidTo) {
    return false;
  }

  const expiryTime = new Date(token.tokenValidTo).getTime();

  if (Number.isNaN(expiryTime)) {
    return false;
  }

  return new Date().getTime() > expiryTime;
};

const parseStoredToken = (raw: string | null): UserToken | null => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const isTokenStructurallyValid = (token: UserToken | null): token is UserToken =>
  !!(token?.sessionToken && token?.userName);

const isTokenUsableNow = (token: UserToken | null): token is UserToken =>
  isTokenStructurallyValid(token) && !isTokenExpired(token);

/** Prefer logged-in token over GUEST, regardless of which storage holds it. */
export const pickPreferredStoredToken = (
  localToken: UserToken | null,
  sessionToken: UserToken | null
): UserToken | null => {
  const candidates = [localToken, sessionToken].filter(isTokenUsableNow);
  return candidates.find((t) => !isGuestName(t.userName)) ?? candidates[0] ?? null;
};

export const readStoredUserToken = (): UserToken | null => {
  return pickPreferredStoredToken(
    parseStoredToken(localStorage.getItem(USER_TOKEN)),
    parseStoredToken(sessionStorage.getItem(USER_TOKEN))
  );
};

/** Read stored token without filtering out expired ones (used to detect refreshable rememberMe tokens). */
export const readRawStoredUserToken = (): UserToken | null => {
  const candidates = [
    parseStoredToken(localStorage.getItem(USER_TOKEN)),
    parseStoredToken(sessionStorage.getItem(USER_TOKEN)),
  ].filter(isTokenStructurallyValid);
  return candidates.find((t) => !isGuestName(t.userName)) ?? candidates[0] ?? null;
};

export const clearStoredUserToken = () => {
  localStorage.removeItem(USER_TOKEN);
  sessionStorage.removeItem(USER_TOKEN);
};

const persistUserToken = (token: UserToken, storage: "local" | "session") => {
  clearStoredUserToken();
  const raw = JSON.stringify(token);
  if (storage === "local") {
    localStorage.setItem(USER_TOKEN, raw);
  } else {
    sessionStorage.setItem(USER_TOKEN, raw);
  }
};

const applySession = (store: any, token: UserToken, asGuest: boolean) => {
  if (token?.sessionToken) {
    authInterceptor(token.sessionToken);
  }
  store.dispatch(asGuest ? setFakeSession(token) : setUserToken(token));
};

const beginGuestSession = (store: any) => {
  if (isFakeSessionInFlight()) {
    return;
  }
  clearStoredUserToken();
  authInterceptor("");
  clearSurveyDraft(store.getState().config.facilityIds);
  store.dispatch(resetBasket());
  store.dispatch(fakeSessionLogIn());
};

const hasSessionExpiredMessage = (payload: any) => {
  const table = payload?.errorTable;
  if (!Array.isArray(table)) {
    return false;
  }
  return table.some(
    (item) => typeof item === "string" && item.toLowerCase().includes(SESSION_EXPIRED_ERROR_MESSAGE)
  );
};

const adoptStoredToken = (store: any, token: UserToken) => {
  const currentSessionToken = store.getState().auth.userToken?.sessionToken;
  if (currentSessionToken === token.sessionToken) {
    authInterceptor(token.sessionToken);
    return;
  }
  applySession(store, token, isGuestName(token.userName));
};

/** Drop the other storage entry when it conflicts with the chosen active token. */
const clearConflictingStorage = (active: UserToken, localToken: UserToken | null, sessionToken: UserToken | null) => {
  if (localToken && localToken.sessionToken !== active.sessionToken) {
    localStorage.removeItem(USER_TOKEN);
  }
  if (sessionToken && sessionToken.sessionToken !== active.sessionToken) {
    sessionStorage.removeItem(USER_TOKEN);
  }
};

const authMiddleware = (store: any) => (next: (action: Action<any>) => void) => (action: any) => {
  if (action.type === login.fulfilled.toString()) {
    const rememberMe = !!action.meta?.arg?.data?.rememberMe;
    const storedToken = {
      ...action.payload,
      rememberMe,
    };
    persistUserToken(storedToken, rememberMe ? "local" : "session");
    store.dispatch(resetBasket());
    applySession(store, storedToken, false);
  }

  if (action.type === logInWithPassword.toString()) {
    const facilityIds = store.getState().config.facilityIds;
    gaEvent(facilityIds, "...", GA_LOGIN, {}, "");
    next(action);
    return store.dispatch(login({ url: LOGIN_USER_URL, data: action.payload }));
  }

  if (action.type === logOut.toString()) {
    clearB2bHomePageUrl();
    beginGuestSession(store);
  }

  if (action.type === replaceWithGuestSession.toString()) {
    beginGuestSession(store);
  }

  if (action.type === refreshUserToken.fulfilled.toString()) {
    const payload = action.payload;
    if (!payload?.sessionToken) {
      beginGuestSession(store);
    } else {
      const storedToken = { ...payload, rememberMe: true, lastRefreshedAt: Date.now() };
      persistUserToken(storedToken, "local");
      applySession(store, storedToken, false);
    }
  }

  if (action.type === refreshUserToken.rejected.toString()) {
    if (!action.meta?.condition) {
      beginGuestSession(store);
    }
  }

  if (action.type === findUserTokenInStorage.toString()) {
    const facilityIds = store.getState().config.facilityIds;
    let localToken = parseStoredToken(localStorage.getItem(USER_TOKEN));
    let sessionToken = parseStoredToken(sessionStorage.getItem(USER_TOKEN));
    let refreshingLoggedInToken = false;

    if (localToken && !isTokenStructurallyValid(localToken)) {
      localStorage.removeItem(USER_TOKEN);
      localToken = null;
    } else if (localToken && isTokenExpired(localToken)) {
      if (localToken.rememberMe && localToken.refreshToken && !store.getState().auth.loading) {
        refreshingLoggedInToken = true;
        store.dispatch(refreshUserToken({ objectIds: facilityIds, refreshToken: localToken.refreshToken }));
      } else {
        localStorage.removeItem(USER_TOKEN);
        localToken = null;
      }
    }

    if (sessionToken && (!isTokenStructurallyValid(sessionToken) || isTokenExpired(sessionToken))) {
      sessionStorage.removeItem(USER_TOKEN);
      sessionToken = null;
    }

    if (!refreshingLoggedInToken) {
      const activeToken = pickPreferredStoredToken(localToken, sessionToken);
      if (activeToken) {
        clearConflictingStorage(activeToken, localToken, sessionToken);
        adoptStoredToken(store, activeToken);
      }
    }
  }

  if (action.type === fakeSessionLogIn.fulfilled.toString()) {
    const b2bToken = qs.parse(window.location.search.slice(1)).B2BSESSION as string;
    const userToken = action.payload;
    if (b2bToken && userToken) {
      const overrideToken = { ...userToken, sessionToken: b2bToken, userName: b2bToken };
      persistUserToken(overrideToken, "session");
      applySession(store, overrideToken, false);
    } else if (userToken?.sessionToken) {
      persistUserToken(userToken, "local");
      applySession(store, userToken, true);
    }
    settleFakeSessionFlight();
  }

  if (action.type === fakeSessionLogIn.rejected.toString() && !action.meta?.condition) {
    settleFakeSessionFlight();
  }

  if (hasSessionExpiredMessage(action.payload)) {
    const currentName = store.getState().auth.userToken?.userName;
    const storedName = readStoredUserToken()?.userName;
    if (!isGuestName(currentName) && !isGuestName(storedName)) {
      beginGuestSession(store);
    }
  }

  next(action);
};

export default authMiddleware;
